import React, { useEffect, useState } from "react";
import { Waves, Wind, Droplets, Thermometer, Loader2, Anchor, Sun, Cloud, CloudRain } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";

const LAT = 11.2528, LON = -85.8703;
const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const wcIcon = (code) => {
  if (code === 0 || code === 1) return Sun;
  if (code >= 51 && code <= 82) return CloudRain;
  return Cloud;
};

// Lower score = better fishing day (calmer seas, less wind & rain)
const score = (d) => d.wave * 3 + d.wind * 0.12 + d.precip * 0.04;

const WeatherWidget = ({ compact = false }) => {
  const [state, setState] = useState({ loading: true, error: false, daily: [], current: null });

  useEffect(() => {
    const marine = `https://marine-api.open-meteo.com/v1/marine?latitude=${LAT}&longitude=${LON}&daily=wave_height_max,wave_period_max&timezone=America%2FManagua`;
    const forecast = `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&daily=temperature_2m_max,temperature_2m_min,wind_speed_10m_max,precipitation_probability_max,weather_code&current=temperature_2m,wind_speed_10m,weather_code&wind_speed_unit=kmh&timezone=America%2FManagua`;
    Promise.all([fetch(marine).then((r) => r.json()), fetch(forecast).then((r) => r.json())])
      .then(([m, f]) => {
        const n = Math.min(7, f.daily.time.length);
        const daily = [];
        for (let i = 0; i < n; i++) {
          daily.push({
            date: f.daily.time[i],
            dow: days[new Date(f.daily.time[i] + "T12:00:00").getDay()],
            tmax: Math.round(f.daily.temperature_2m_max[i]),
            tmin: Math.round(f.daily.temperature_2m_min[i]),
            wind: Math.round(f.daily.wind_speed_10m_max[i]),
            precip: f.daily.precipitation_probability_max[i] ?? 0,
            code: f.daily.weather_code[i],
            wave: m.daily?.wave_height_max?.[i] ?? 0,
            period: m.daily?.wave_period_max?.[i] ?? 0,
          });
        }
        const best = daily.reduce((a, b) => (score(b) < score(a) ? b : a), daily[0]);
        daily.forEach((d) => { d.best = d.date === best.date; });
        setState({ loading: false, error: false, daily, current: { temp: Math.round(f.current.temperature_2m), wind: Math.round(f.current.wind_speed_10m), code: f.current.weather_code } });
      })
      .catch(() => setState({ loading: false, error: true, daily: [], current: null }));
  }, []);

  if (state.loading) return (
    <div className="rounded-2xl border border-white/10 bg-[#0e2740] p-10 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#f5a524]" /></div>
  );
  if (state.error) return (
    <div className="rounded-2xl border border-white/10 bg-[#0e2740] p-8 text-center text-slate-400">Marine forecast is temporarily unavailable. Please try again shortly.</div>
  );

  const CurIcon = wcIcon(state.current.code);
  const shown = compact ? state.daily.slice(0, 5) : state.daily;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0e2740] overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-4 bg-[#071726] px-6 py-4">
        <div className="flex items-center gap-3">
          <CurIcon className="h-9 w-9 text-[#f5a524]" />
          <div>
            <p className="font-display text-3xl text-white leading-none">{state.current.temp}°C</p>
            <p className="text-xs text-slate-400 mt-1">San Juan del Sur · now</p>
          </div>
        </div>
        <div className="flex items-center gap-5 text-sm text-slate-300">
          <span className="flex items-center gap-1.5"><Wind className="h-4 w-4 text-[#5cb3e6]" /> {state.current.wind} km/h</span>
          <span className="flex items-center gap-1.5"><Waves className="h-4 w-4 text-[#0e9488]" /> {shown[0]?.wave?.toFixed(1)} m swell</span>
        </div>
      </div>

      <div className={`grid ${compact ? "grid-cols-5" : "grid-cols-3 sm:grid-cols-4 md:grid-cols-7"} divide-x divide-white/5`}>
        {shown.map((d) => {
          const Icon = wcIcon(d.code);
          return (
            <div key={d.date} className={`p-4 text-center relative ${d.best ? "bg-[#f5a524]/10" : ""}`}>
              {d.best && <span className="absolute top-1.5 left-1/2 -translate-x-1/2 rounded-full bg-[#f5a524] px-2 py-0.5 text-[9px] font-bold text-[#0a1f33] uppercase tracking-wide flex items-center gap-1"><Anchor className="h-2.5 w-2.5" /> Best</span>}
              <p className={`font-heading text-sm mt-3 ${d.best ? "text-[#f5a524]" : "text-slate-300"}`}>{d.dow}</p>
              <Icon className={`h-6 w-6 mx-auto my-2 ${d.best ? "text-[#f5a524]" : "text-slate-400"}`} />
              <p className="text-white text-sm font-semibold">{d.tmax}°</p>
              <div className="mt-2 space-y-1 text-[11px] text-slate-400">
                <p className="flex items-center justify-center gap-1"><Waves className="h-3 w-3" /> {d.wave.toFixed(1)}m</p>
                <p className="flex items-center justify-center gap-1"><Wind className="h-3 w-3" /> {d.wind}</p>
                <p className="flex items-center justify-center gap-1"><Droplets className="h-3 w-3" /> {d.precip}%</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-t border-white/5">
        <p className="text-[11px] text-slate-500">Live marine data · Open-Meteo. Lower swell &amp; wind = better fishing.</p>
        <Link to="/plan-your-trip"><Button size="sm" className="btn-gold h-8 text-xs">Book the best day</Button></Link>
      </div>
    </div>
  );
};

export default WeatherWidget;
